import {
  Component, computed,
  ElementRef,
  HostListener,
  inject,
  input,
  OnChanges,
  output, signal,
  SimpleChanges,
} from '@angular/core'
import { DateOperationPickerComponent } from './date-operation-picker.component'
import { format } from 'date-fns'
import { dateOnlyFormat, monthOnlyFormat } from 'src/app/shared/utilities/date'
import { MatIcon } from '@angular/material/icon'
import { v4 as uuid } from 'uuid'
import { TZDate  } from '@date-fns/tz'
import {
  DateOperation,
  DateOperationDisplay,
  DateRangeFilterStateValue,
} from '../../shared/services/filter/date-range-filter'
import { DateRangeDateComponent } from './date-range-date.component'
import { isEqual } from 'lodash'

const emptyValue: DateRangeFilterStateValue = { operation: null, value: null }

const isOutsideClick = (container: Element, target: Element | null): boolean => {
  let parent: Node | null = target
  while (parent) {
    if (parent === document) {
      return true
    } else if (parent === container || (parent as Element).classList?.contains('ng-star-inserted')) {
      return false
    } else {
      parent = parent.parentNode
    }
  }
  return false
}

@Component({
  selector: 'div[dateRangeFilterPicker]',
  imports: [
    MatIcon,
    DateOperationPickerComponent,
    DateRangeDateComponent,
  ],
  styles: `
    .popover-wrapper {
      position: relative;
      width: 100%;
      padding: 0.5rem 0 0;
      border: none;

      &.disabled {
        opacity: 0.5;
        cursor: none;
      }

      input {
        width: 100%;
        padding: 0.5rem 1.75rem 0.25rem 0.25rem;
        border: none;
        background: white;
        text-align: left;
        border-bottom: 1px solid black;
        font-size: 0.9rem;
        line-height: 1.25rem;

        &:focus {
          outline: none;
        }
      }

      .calendar-icon {
        position: absolute;
        top: 0.75rem;
        right: 0.25rem;
      }

      .popover {
        display: none;
        position: absolute;
      }

      .popover.open {
        z-index: 3;
        display: flex;
        flex-direction: column;
        min-height: 3rem;
        width: 334px;
        max-width: unset;
        right: 0;
        top: calc(100% + 0.5rem);
        border: 1px solid black;
        border-radius: 0;
        padding: 0.5rem 1rem;
        background: white;


        &::before {
          content: ' ';
          border: none;
          border-top: 1px solid black;
          border-right: 1px solid black;
          width: 0.5rem;
          height: 0.5rem;
          transform: rotate(-45deg);
          position: absolute;
          top: calc(-0.25rem - 1px);
          background: linear-gradient(
                          45deg,
                          rgba(255, 255, 255, 0) 0%,
                          rgba(255, 255, 255, 0) calc(50% - 1px),
                          rgba(255, 255, 255, 1) calc(50% - 1px),
                          rgba(255, 255, 255, 1) 100%
          );
          right: 1rem;
        }
      }

      label {
        position: absolute;
        top: 1rem;
        left: 1rem;
        opacity: 0.75;
        width: 0;
        white-space: nowrap;
      }

      input::placeholder {
        opacity: 0;
      }

      input:not(:placeholder-shown) + label {
        transform: scale(0.75) translateY(-1.25rem);
      }
    }

    .date-picker-wrapper {
      width: 300px;
      display: grid;
      margin-top: 0.25rem;
      grid-template:
        'd1 d1' auto
        'cb sb' auto / 1fr 1fr;
      row-gap: 0.25rem;
      column-gap: 1rem;

      &.operation {
        grid-template:
        'op op' auto
        'sp sp' 0.75rem
        'd1 d1' auto
        'cb sb' auto / 1fr 1fr;
      }

      &.operation.range {
        grid-template:
          'op op' auto
          'sp sp' 0.75rem
          'd1 d1' auto 
          'rb rb' 0.75rem 
          'd2 d2' auto
          'cb sb' auto / 1fr 1fr;
      }

      .op {
        grid-area: op;
      }

      .d1 {
        grid-area: d1;
      }

      .d2 {
        grid-area: d2;
      }

      .sb {
        grid-area: sb;
        border: 1px solid black;
        border-radius: 0.25rem;
        background-color: green;
        color: white;

        &:disabled {
          opacity: 0.5;
        }
      }

      .cb {
        grid-area: cb;
        border: 1px solid gray;
        border-radius: 0.25rem;
        background-color: white;
        color: black;
      }
    }`,
  template: `
    <div class="popover-wrapper" [class.disabled]="disabled()" (keyup)="handleKeyUp($event)">
      <input
          type="button"
          [id]="id()"
          [placeholder]="'none'"
          [value]="displayValue()"
          (click)="toggle()"
      />
      <mat-icon
          class="calendar-icon"
          svgIcon="calendar_month"
          (click)="toggle()"
      />
      <div class="popover" [class.open]="open()">
        <div
            class="date-picker-wrapper"
            [class.operation]="showOperation()"
            [class.range]="operation() === 'between'"
        >
          @if (showOperation()) {
            <div
                class="op"
                dateOperationPicker
                [showRange]="showRange()"
                [value]="operation()"
                (valueChange)="operation.set($event)"
            >
            </div>
          }
          <app-date-range-date
              class="d1"
              [class.mb-2]="operation() !== 'between'"
              [value]="startDate()"
              [onlyMonth]="onlyMonth()"
              [showTime]="showTime()"
              [showSeconds]="showSeconds()"
              [tz]="viewAsTZ()"
              (valueChange)="startDate.set($event)"
              (enter)="onSubmit()"
          />
          @if (operation() === 'between') {
            <app-date-range-date
                class="d2 mb-2"
                [value]="endDate()"
                [onlyMonth]="onlyMonth()"
                [showTime]="showTime()"
                [showSeconds]="showSeconds()"
                [endOfDay]="true"
                [tz]="viewAsTZ()"
                (valueChange)="endDate.set($event)"
                (enter)="onSubmit()"
            />
          }
          <button class="cb" (click)="onCancel()">Cancel</button>
          <button class="sb" [disabled]="!submitEnabled()" (click)="onSubmit()">Submit</button>
        </div>
      </div>
    </div>
  `,
})
export class DateRangeFilterPickerComponent implements OnChanges {
  private readonly elementRef = inject(ElementRef)

  readonly value = input<DateRangeFilterStateValue | undefined>()
  readonly id = input(uuid())
  readonly disabled = input(false)
  readonly showOperation = input(false)
  readonly showRange = input(false)
  readonly showTime = input(false)
  readonly showSeconds = input(false)
  readonly onlyMonth = input(false)
  readonly viewAsTZ = input('EST')

  readonly valueChange = output<DateRangeFilterStateValue>()

  protected readonly operation = signal<DateOperation>('eq')
  protected readonly startDate = signal<Date | null>(null)
  protected readonly endDate = signal<Date | null>(null)

  protected readonly open = signal(false)

  protected readonly currentValue = computed((): DateRangeFilterStateValue => {
    const operation = this.operation()
    const startDate = this.startDate()
    if (startDate) {
      if (operation === 'between') {
        const endDate = this.endDate()
        if (endDate && endDate > startDate) {
          return { operation, value: [startDate, endDate] }
        }
      } else {
        return { operation, value: startDate }
      }
    }
    return emptyValue
  })

  protected displayValue = computed((): string => {
    const { operation, value } = this.currentValue()
    if (operation && value) {
      const pattern = this.getDisplayFormat()
      const dateDisplay = [value].flat()
        .map(d => format(new TZDate(d,  this.viewAsTZ()), pattern))
        .join(' & ')
      return operation === 'eq' ? dateDisplay : `${DateOperationDisplay[operation]} ${dateDisplay}`
    } else {
      return 'None Selected'
    }
  })

  protected submitEnabled = computed((): boolean =>
    this.currentValue().operation !== null && !isEqual(this.value(), this.currentValue()),
  )

  private getDisplayFormat(): string {
    if (this.onlyMonth()) {
      return monthOnlyFormat
    } else if (this.showTime()) {
      return `${dateOnlyFormat} HH:mm${this.showSeconds() ? ':ss' : ''}`
    } else {
      return dateOnlyFormat
    }
  }

  @HostListener('document:click', ['$event.target'])
  handleOutsideClick(target: EventTarget | null) {
    if (this.open() && isOutsideClick(this.elementRef.nativeElement, target as Element | null)) {
      this.toggle()
    }
  }

  protected handleKeyUp(evt: KeyboardEvent){
    if(evt.key === 'Enter') {
      this.onSubmit()
    }
  }

  protected onSubmit() {
    if (this.submitEnabled()) {
      this.valueChange.emit(this.currentValue())
      void this.toggle()
    }
  }

  private initState() {
    const { operation, value } = this.value() ?? emptyValue
    this.operation.set(operation ?? 'eq')
    const [startDate, endDate] = [value].flat()
    this.startDate.set(startDate ?? null)
    this.endDate.set(endDate ?? null)
  }

  private async grabFocus() {
    const textInput = document.querySelector('.text-input')
    if (textInput) {
      while (!textInput.checkVisibility()) {
        await new Promise((res) => setTimeout(res, 10))
      }
      (textInput as HTMLInputElement).focus()
    }
  }

  protected onCancel() {
    this.initState()
    this.toggle()
  }

  protected toggle() {
    if (!this.disabled()) {
      const opening = !this.open()
      this.open.set(opening)
      if (opening) {
        void this.grabFocus()
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['value']) {
      this.initState()
    }
  }
}
